import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link } from 'react-router-dom'

export default function SignUp() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [emailConsent, setEmailConsent] = useState(false) // Marketing email consent
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('') // Success message state
  const [loading, setLoading] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const { signUp } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (password !== confirmPassword) {
      return setError('Passwords do not match')
    }

    if (password.length < 6) {
      return setError('Password must be at least 6 characters')
    }

    setLoading(true)

    const { error: signUpError } = await signUp(email, password, emailConsent)

    if (signUpError) {
      setError(signUpError.message)
      setLoading(false)
    } else {
      setLoading(false)
      // Show success message with email verification instructions
      setSuccess('account_created')
      
      // Send notification to admin about new signup
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        await fetch(`${backendUrl}/api/notifications/new-signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            timestamp: new Date().toISOString()
          })
        });
      } catch (notifError) {
        console.log('Notification send failed (non-critical):', notifError);
        // Don't show error to user - notification is non-critical
      }
      
      // Auto-navigate to login after 8 seconds (give user time to read)
      setTimeout(() => {
        setIsTransitioning(true)
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      }, 8000)
    }
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
    >
      {/* Animated Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className={`fixed inset-0 w-full h-full object-cover transition-all duration-1000`}
        style={{ 
          zIndex: 0,
          filter: isTransitioning ? 'brightness(1.2)' : 'brightness(1.0)'
        }}
      >
        <source src="/assets/world-leaders-nuke.mp4" type="video/mp4" />
        {/* Fallback to static image if video fails to load */}
        <img 
          src="/assets/nuke-ride-bg.png" 
          alt="Background"
          className="w-full h-full object-cover"
        />
      </video>

      {/* Dark overlay for readability - fade out during transition */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-1000 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ zIndex: 1 }}
      />
      
      {/* Content - fade out during transition */}
      <div 
        className={`relative bg-slate-900/95 backdrop-blur-md p-8 rounded-lg w-full max-w-md border-4 border-amber-500/50 shadow-2xl transition-opacity duration-1000 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          zIndex: 2,
          boxShadow: '0 0 40px rgba(245, 158, 11, 0.4), 0 0 80px rgba(245, 158, 11, 0.2), inset 0 2px 20px rgba(0,0,0,0.5)'
        }}
      >
        <h1 className="text-4xl font-bold text-center mb-2 font-mono tracking-wider"
          style={{
            color: '#f472b6',
            textShadow: '0 0 20px rgba(244, 114, 182, 0.8), 0 2px 4px rgba(0,0,0,0.9)',
          }}
        >
          Global Thermo III
        </h1>
        <p className="text-center text-slate-300 mb-8 text-sm tracking-wide">Create Your Commander Account</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* SUCCESS MESSAGE - Account Created */}
          {success === 'account_created' && (
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400 rounded-lg p-5 animate-pulse">
              <div className="text-center mb-3">
                <div className="text-5xl mb-2">✅ 🎉 ☢️</div>
                <h3 className="text-xl font-bold text-green-300 mb-2">Account Created!</h3>
              </div>
              
              <div className="bg-slate-900/50 border border-green-500/30 rounded p-4 mb-3">
                <p className="text-green-200 text-sm leading-relaxed mb-3 text-center">
                  <span className="text-2xl mr-2">🎮</span>
                  <strong className="text-yellow-300">YOUR COMMANDER ACCOUNT IS READY!</strong>
                </p>
                <p className="text-slate-300 text-sm leading-relaxed mb-3 text-center">
                  Registered as:
                  <br />
                  <span className="text-green-300 font-mono font-bold">{email}</span>
                </p>
                <div className="border-t border-green-500/30 pt-3 mt-3">
                  <p className="text-slate-200 text-sm leading-relaxed text-center mb-3">
                    <span className="text-2xl mr-2">🚀</span>
                    <strong className="text-yellow-300">You can now login and start playing!</strong>
                  </p>
                  <div className="bg-blue-500/10 border border-blue-400/30 rounded p-3 mt-3">
                    <p className="text-blue-200 text-sm leading-relaxed text-center">
                      <span className="text-xl mr-2">📧</span>
                      <strong>Check your email for exclusive bonuses!</strong>
                      <br />
                      <span className="text-xs text-slate-400 mt-1 block">
                        Verify to unlock early access to new countries & updates
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
                <p className="text-yellow-200 text-xs leading-relaxed text-center">
                  <span className="text-lg mr-1">☢️ 🚀 💣</span>
                  <strong>Global thermonuclear strategy awaits!</strong>
                  <span className="text-lg ml-1">⚛️ 🌍 ☢️</span>
                </p>
              </div>

              <p className="text-slate-400 text-xs text-center mt-3">
                Redirecting to login in 8 seconds...
              </p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2 tracking-wide">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-slate-800/80 border-2 border-slate-700 rounded-lg focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 text-white placeholder-slate-500 transition-all font-mono"
              style={{
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
              }}
              placeholder="commander@nation.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-200 mb-2 tracking-wide">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-4 py-3 bg-slate-800/80 border-2 border-slate-700 rounded-lg focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 text-white placeholder-slate-500 transition-all font-mono"
              style={{
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
              }}
              placeholder="••••••••"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-200 mb-2 tracking-wide">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-4 py-3 bg-slate-800/80 border-2 border-slate-700 rounded-lg focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 text-white placeholder-slate-500 transition-all font-mono"
              style={{
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.4)'
              }}
              placeholder="••••••••"
            />
          </div>

          {/* EMAIL CONSENT CHECKBOX - Themed for GTNW3 */}
          <div className="bg-slate-800/50 border-2 border-pink-500/30 rounded-lg p-4">
            <label className="flex items-start space-x-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={emailConsent}
                onChange={(e) => setEmailConsent(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-2 border-pink-500 bg-slate-900 text-pink-600 focus:ring-2 focus:ring-pink-500 focus:ring-offset-0 cursor-pointer transition-all"
                style={{
                  accentColor: '#ec4899'
                }}
              />
              <div className="flex-1">
                <p className="text-slate-200 text-sm leading-relaxed group-hover:text-pink-300 transition-colors">
                  <span className="text-base mr-1">📡</span>
                  <strong className="text-pink-400">Subscribe to Intelligence Briefings</strong>
                </p>
                <p className="text-slate-400 text-xs leading-relaxed mt-1">
                  Receive classified updates on new territories, strategic expansions, weapons systems, and other critical intelligence regarding Global Thermo III. You may unsubscribe from these communications at any time.
                </p>
                <p className="text-slate-500 text-xs mt-2 italic">
                  By checking this box, you authorize us to send you promotional emails about game updates, new content releases, and special events. Your email will not be shared with third parties.
                </p>
              </div>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-600 to-fuchsia-600 text-white font-bold py-4 px-4 rounded-lg hover:from-pink-500 hover:to-fuchsia-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider text-lg border-2 border-pink-400/50 relative overflow-hidden group"
            style={{
              boxShadow: '0 0 20px rgba(244, 114, 182, 0.5), 0 4px 12px rgba(0,0,0,0.5)',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            <span className="relative z-10">{loading ? 'Creating Account...' : 'Sign Up'}</span>
            <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-fuchsia-400 opacity-0 group-hover:opacity-30 transition-opacity" />
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-300 text-sm">
            Already have an account?{' '}
            <Link 
              to="/login" 
              className="text-pink-400 hover:text-pink-300 font-bold tracking-wide transition-colors"
              style={{ textShadow: '0 0 10px rgba(244, 114, 182, 0.5)' }}
            >
              Login
            </Link>
          </p>
        </div>
      </div>
      
      {/* Animated glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
    </div>
  )
}
